import React, { useRef, useState } from "react";
import "./Contact.css";
import { MdOutlineEmail } from "react-icons/md";
import { BsWhatsapp } from "react-icons/bs";
import emailjs from "@emailjs/browser";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import { TbBrandLinkedin } from "react-icons/tb";
import AnimatedHeadings from "../animatedHeadings/AnimatedHeadings";

/* =======================
   Validation Schema
======================= */
const schema = yup.object({
  name: yup.string().required("Full name is required").min(2, "Name is too short"),
  email: yup.string().required("Email is required").email("Invalid email address"),
  message: yup
    .string()
    .required("Message is required")
    .max(400, "Message must not exceed 400 characters"),
});

const Contact = () => {
  const formRef = useRef();
  const recaptchaRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const sendEmail = async () => {
    if (isLoading) return;

    // Check reCAPTCHA
    const recaptchaValue = recaptchaRef.current?.getValue();
    if (!recaptchaValue) {
      alert("Please verify that you are not a robot.");
      return;
    }

    setIsLoading(true);

    try {
      // 1️⃣ Send admin notification
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        process.env.REACT_APP_EMAILJS_USER_ID,
      );

      alert("Message sent successfully!");
      reset();
      recaptchaRef.current.reset();
    } catch (error) {
      console.error("Failed to send email:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact">
      <AnimatedHeadings smallTitle="Reach Out" mainTitle="Contact Me" />

      <div className="container contact__container">
        <div className="contact__options">
          <article className="contact__option">
            <BsWhatsapp />
            <h4>Whatsapp</h4>
            <h5>+216 22 058 086</h5>
            <a
              href="https://api.whatsapp.com/send?phone=21622058086"
              target="_blank"
              rel="noreferrer"
            >
              Send a message
            </a>
          </article>

          <article className="contact__option">
            <MdOutlineEmail size={20} />
            <h4>Email</h4>
            <h5>mahdichihi95@gmail.com</h5>
            <a href="mailto:mahdichihi95@gmail.com">Send a message</a>
          </article>

          <article className="contact__option">
            <TbBrandLinkedin size={20} />
            <h4>LinkedIn</h4>
            <h5>mahdichihi</h5>
            <a
              href="https://www.linkedin.com/messaging/compose/?recipient=mahdichihi"
              target="_blank"
              rel="noreferrer"
            >
              Send a message
            </a>
          </article>
        </div>

        {/* FORM */}
        <form ref={formRef} onSubmit={handleSubmit(sendEmail)}>
          <div className="form__group">
            <input
              type="text"
              className={errors.name ? "error" : ""}
              placeholder="Your Full Name"
              autoComplete="name"
              {...register("name")}
            />
            {errors.name && <small className="error-text">{errors.name.message}</small>}
          </div>
          <div className="form__group">
            <input
              type="email"
              className={errors.email ? "error" : ""}
              placeholder="Your Email Address"
              autoComplete="email"
              {...register("email")}
            />
            {errors.email && <small className="error-text">{errors.email.message}</small>}
          </div>
          <div className="form__group">
            <textarea
              className={errors.message ? "error" : ""}
              rows="7"
              placeholder="Your Message..."
              maxLength={400}
              {...register("message")}
            />
            {errors.message && <small className="error-text">{errors.message.message}</small>}
          </div>

          <div className="recaptcha__container">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              theme="dark"
              size="compact"
              badge="inline"
            />
          </div>

          <button type="submit" className="btn btn-primary submit-btn" disabled={isLoading}>
            {isLoading ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
